// Composer Studio — 템플릿 갱신(PATCH) body 조립
//
// 에디터의 저장 경로가 보내는 **필드 집합의 정본**. EditorPage 안에 있던 것을 끌어냈다 —
// 초안 일괄 편집(양식 업로드 결과 모달의 반복 제안 적용)처럼 에디터에 로드하지 않은 템플릿을
// 저장하는 경로가 생겼기 때문이다. 필드를 손으로 골라 적는 두 번째 목록이 생기면 dataSources·
// importMeta 같은 축이 한쪽에서만 빠져 **조용히 소실**된다(이 파일의 주석들이 전부 그 사고 기록).
//
// ⚠️ 여기의 필드 목록은 `./templateEditableLoad`의 `toEditableTemplate`(로드)과 **대칭**이어야
//    한다. 한쪽에만 있는 필드는 유령(저장돼도 안 보임) 또는 소실(로드 누락 상태로 저장해 DB를
//    비움)이 된다. 이 대칭은 이제 주석이 아니라 `./templateFieldSymmetry.test.ts`가 지킨다 —
//    ReportTemplate에 필드가 추가되면 그 spec이 컴파일 단계에서 먼저 막는다.
import type { ReportTemplate, TemplatePolicy } from '@safetyhub/shared';
import type { UpdateReportTemplateBody } from '../../lib/api';

/** 갱신 body — key는 경로로 식별하므로 제외한 나머지 필드. */
export function toUpdateBody(t: ReportTemplate): UpdateReportTemplateBody {
  return {
    name: t.name,
    // 제목 규칙(2026-08-17) — 입력을 비우면 undefined가 되는데 그대로 보내면 JSON 직렬화에서
    // 필드가 탈락해 서버 update가 skip(해제가 영원히 미반영, entryUserInput 실사고 동형).
    // null로 보내 명시적 해제(DB NULL) — policy와 같은 null 계약.
    titleTemplate: t.titleTemplate ?? null,
    schemaVersion: t.schemaVersion,
    systemType: t.systemType,
    pluginKey: t.pluginKey,
    // 진입 전 사용자 입력 스위치 — toEditableTemplate(로드)과 대칭(한쪽만 있으면 silent 미반영/소실).
    entryUserInput: t.entryUserInput,
    page: t.page,
    body: t.body,
    approvalPlacement: t.approvalPlacement,
    // ⚠️ dataDefs/approvalLineMeta도 갱신에 포함해야 함(누락 시 결재단계·데이터정의 편집이
    //    저장에 반영 안 됨 + 저장 시 DB 값까지 비워질 수 있음). 서버 update DTO/서비스가 둘 다 영속.
    approvalLineMeta: t.approvalLineMeta,
    dataDefs: t.dataDefs,
    // 조립형 데이터 소스(Phase B) — 갱신 payload에도 포함해야 편집이 반영되고 DB 값이 보존됨.
    //    ⚠️ toEditableTemplate(로드)과 대칭 — 한쪽만 있으면 편집이 silent 미반영/소실.
    dataSources: t.dataSources,
    // 카탈로그 파라미터 지정(2026-08-06 결정 개정 1) — toEditableTemplate(로드)과 대칭(한쪽만 있으면 silent 미반영/소실).
    //   ⚠️ **마지막 지정을 해제하면 undefined**가 되는데, 그대로 보내면 JSON 직렬화에서 필드가 탈락해
    //   서버 update가 skip → 해제가 영원히 미반영된다(entryUserInput 2026-07-25 실사고 동형).
    //   서버 DTO가 null을 받지 않으므로(타입이 Record) **빈 객체로 명시 해제**한다 — 해소는
    //   `catalogParams?.[k]?.[p]` 옵셔널 체인이라 `{}`와 미설정이 동치다.
    catalogParams: t.catalogParams ?? {},
    // 작성 페이지 위젯 트리(2026-07-08 결정) — toEditableTemplate(로드)과 대칭(한쪽만 있으면 silent 미반영/소실).
    composerWidgetTree: t.composerWidgetTree,
    // 작성 폼 chrome 구성(2026-07-09 결정) — toEditableTemplate(로드)과 대칭(한쪽만 있으면 silent 미반영/소실).
    composeChrome: t.composeChrome,
    // 상세 페이지 chrome 상태별 표시 구성(2026-07-21 결정) — toEditableTemplate(로드)과 대칭(한쪽만 있으면 silent 미반영/소실).
    detailChrome: t.detailChrome,
    // 위임 작성 메타(2026-07-06 결정) — toEditableTemplate(로드)과 대칭(한쪽만 있으면 silent 미반영/소실).
    delegationMeta: t.delegationMeta,
    // 양식 전역 변수(2026-07-07 결정) — toEditableTemplate(로드)과 대칭(한쪽만 있으면 silent 미반영/소실).
    variables: t.variables,
    // 노출·중복 정책(2026-07-26) — 정규화 후 빈 정책은 null(명시적 해제)로 보낸다.
    // undefined면 JSON 직렬화에서 필드가 탈락해 서버 update가 skip → 해제가 영원히
    // 미반영되는 함정(entryUserInput 2026-07-25 실사고 동형). null은 서버가 DB NULL로 클리어.
    policy: normalizeTemplatePolicy(t.policy) ?? null,
    // 양식 업로드 초안 메타(2026-07-27) — 적용·무시로 repeatHints가 소진되면 편집 상태에서
    // importMeta가 제거되므로 null(명시적 해제)로 보내 DB를 비운다(policy와 같은 null 계약).
    importMeta: t.importMeta ?? null,
    // 모사 데이터 프리셋(2026-09-03) — 마지막 프리셋을 지우면 undefined가 되는데 그대로 보내면
    // JSON 직렬화에서 필드가 탈락해 서버 update가 skip(해제 미반영 함정). 서버 DTO가 배열만
    // 받으므로 catalogParams와 같은 계약으로 **빈 배열로 명시 해제**한다(소비는 목록 순회라 동치).
    simulationPresets: t.simulationPresets ?? [],
    // 기본 모사 프리셋 id(2026-09-04) — 체크 해제 시 undefined가 되는데 그대로 보내면 JSON
    // 직렬화에서 필드가 탈락해 서버 update가 skip(해제 미반영 함정). titleTemplate과 같은
    // null 계약으로 명시 해제(DB NULL).
    defaultSimulationPresetId: t.defaultSimulationPresetId ?? null,
  };
}

/**
 * 노출·중복 정책 저장 정규화 — 기본값과 같은 항목은 기록하지 않는다(미지정=기본).
 * duplicatePolicy는 'allow'만 명시 저장('block'=기본이라 제거), audience 배열은 빈 배열이면
 * 축 제거(빈 배열 저장 금지 — 전체 공개는 미지정으로 표현). 남는 축이 없으면 undefined 반환 —
 * 호출부(toUpdateBody)가 null로 바꿔 서버에 명시적 해제를 전달한다.
 *
 * ⚠️ **원본 정책을 spread로 출발해, 이 화면이 편집하는 두 축만 덮는다**(2026-09-02 P5-b 수정).
 * 원래는 `{}`에서 시작해 duplicatePolicy·audience만 다시 담았는데, `toUpdateBody`가 그 결과를
 * 그대로 PATCH body에 싣기 때문에 **에디터에서 무엇을 고쳐 저장하든 당시의 `policy.seed`·
 * `policy.sourceOrder`·`policy.split`이 DB에서 사라졌다**(앞의 둘은 2026-09-04 결정으로
 * policy에서 제거 — 지금 남는 하위 축은 `split`이다). 실제 소실 경로였고, 저장 경로 둘
 * (에디터 / FormImportModal 초안 일괄 편집)이 이 함수를 공유하므로 양쪽 모두 그랬다.
 *
 * 보존할 축을 **손으로 나열하지 않는 것이 수정의 핵심**이다 — 이 버그 자체가 손나열 재조립의
 * 산물이라, 목록으로 고치면 `TemplatePolicy`에 다음 축이 생기는 순간 같은 사고가 재발한다
 * (`memory/feedback-explicit-field-mapping-vs-spread`의 drop 면). spread는 아직 존재하지 않는
 * 축까지 자동으로 통과시킨다.
 *
 * `templateFieldSymmetry.test.ts`가 못 잡은 이유는 그 spec이 **최상위 키**만 대조하기
 * 때문이다 — `policy` 키는 남아 있으니 통과했다. 그래서 그 사각지대를 겨냥한 케이스를
 * 같은 spec에 함께 넣었다("템플릿 정책 하위 축 보존" — 미지의 축 왕복 포함).
 */
export function normalizeTemplatePolicy(
  policy: TemplatePolicy | undefined,
): TemplatePolicy | undefined {
  if (!policy) return undefined;
  // 원본이 출발점 — 편집하지 않는 축은 손대지 않는다(무엇이 있는지 알 필요도 없다).
  const next: TemplatePolicy = { ...policy };

  // duplicatePolicy: 'allow'만 명시 저장. 'block'은 기본이라 축을 지운다(미지정과 같은 표시).
  if (next.duplicatePolicy !== 'allow') delete next.duplicatePolicy;

  // audience: 빈 배열 축은 지우고, 두 축이 모두 비면 audience 자체를 지운다(전체 공개=미지정).
  const categories = policy.audience?.organizationCategories?.filter(Boolean) ?? [];
  const codes = policy.audience?.industryCodes?.filter(Boolean) ?? [];
  if (categories.length > 0 || codes.length > 0) {
    next.audience = {
      ...(categories.length > 0 ? { organizationCategories: categories } : {}),
      ...(codes.length > 0 ? { industryCodes: codes } : {}),
    };
  } else {
    delete next.audience;
  }

  // 접는 판정(=undefined→null 해제)은 **값 있는 축이 하나도 없을 때만**. spread는 값이
  // undefined인 키도 옮기므로 키 개수가 아니라 값으로 센다(`{split: undefined}`를 정책이
  // 있는 것으로 세면 명시적 해제가 `{}` 저장으로 바뀐다).
  if (!Object.values(next).some((v) => v !== undefined)) return undefined;
  return next;
}
